import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { catchError, tap, throwError } from 'rxjs';
import { EnovaAlertsComponent } from '../../core/utils/enova-alerts/enova-alerts.component';
import { AgentsService } from '../../core/services/agents/agents.service';
import {
  GetAllAgentsAction,
  GetAgentByUuidAction,
  CreateAgentAction,
  UpdateAgentAction,
  DeleteAgentAction,
} from './agents.actions';
import { IAgente } from '../../core/interfaces/agents.interface';

export interface AgentsStateModel {
  agents: IAgente[];
  selectedAgent: IAgente | null;
  loading: boolean;
}

@State<AgentsStateModel>({
  name: 'agents',
  defaults: {
    agents: [],
    selectedAgent: null,
    loading: false,
  },
})
@Injectable()
export class AgentsState {
  @Selector()
  static getAllAgents(state: AgentsStateModel): IAgente[] {
    return state?.agents ?? [];
  }

  @Selector()
  static getSelectedAgent(state: AgentsStateModel): IAgente | null {
    return state?.selectedAgent ?? null;
  }

  @Selector()
  static getLoading(state: AgentsStateModel): boolean {
    return state?.loading ?? false;
  }

  constructor(
    private agentsService: AgentsService,
    private dialog: MatDialog
  ) {}

  @Action(GetAllAgentsAction)
  getAllAgentsAction(ctx: StateContext<AgentsStateModel>) {
    ctx.patchState({ loading: true });
    return this.agentsService.getAllAgents().pipe(
      tap(response => {
        ctx.patchState({
          agents: response,
          loading: false,
        });
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al obtener los agentes',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(GetAgentByUuidAction)
  getAgentByUuidAction(
    ctx: StateContext<AgentsStateModel>,
    { payload }: GetAgentByUuidAction
  ) {
    ctx.patchState({ loading: true });
    return this.agentsService.getAgentByUuid(payload).pipe(
      tap(response => {
        ctx.patchState({
          selectedAgent: response,
          loading: false,
        });
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al obtener el agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(CreateAgentAction)
  createAgentAction(
    ctx: StateContext<AgentsStateModel>,
    { payload }: CreateAgentAction
  ) {
    ctx.patchState({ loading: true });
    return this.agentsService.createAgent(payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El agente se ha creado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllAgentsAction());
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al crear el agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(UpdateAgentAction)
  updateAgentAction(
    ctx: StateContext<AgentsStateModel>,
    { payload }: UpdateAgentAction
  ) {
    ctx.patchState({ loading: true });
    return this.agentsService.updateAgent(payload.uuid, payload.data).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: '¡El agente se ha actualizado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllAgentsAction());
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al actualizar el agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }

  @Action(DeleteAgentAction)
  deleteAgentAction(
    ctx: StateContext<AgentsStateModel>,
    { payload }: DeleteAgentAction
  ) {
    ctx.patchState({ loading: true });
    return this.agentsService.deleteAgent(payload).pipe(
      tap(() => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'success',
            text: 'El agente se ha eliminado de forma exitosa!',
            hiddeCancelBtn: true,
          },
        });
        ctx.dispatch(new GetAllAgentsAction());
      }),
      catchError(error => {
        ctx.patchState({ loading: false });
        this.dialog.open(EnovaAlertsComponent, {
          width: 'auto',
          data: {
            type: 'error',
            text: 'Ha ocurrido un error al eliminar el agente',
            hiddeCancelBtn: true,
          },
        });
        return throwError(() => error);
      })
    );
  }
}

